// Right-side drawer: CP header, call button, add-remark form, reverse-chron history.
import { useEffect, useState } from "react";
import {
  Drawer, Box, Typography, IconButton, Button, Select, MenuItem, TextField,
  CircularProgress, Chip, Divider, useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CallIcon from "@mui/icons-material/Call";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getCpActivity, addCpActivity, CP_SOURCING_OUTCOMES } from "../../Services/CpSourcingApis";
import { TIER_META } from "./cpSourcingTheme";
import { fmtDateTime } from "./cpSourcingFormat";
import CpDetailMobile from "./Mobile/CpDetailMobile";

export default function CpDetailDrawer({ cp, onClose, onCall, onSaved }) {
  const loggedInUser = useSelector((s) => s?.Auth?.user);
  const userId = loggedInUser?.userId || loggedInUser?.id;
  // Same breakpoint the Partners mobile view uses (ChannelPartner.jsx).
  const isMobile = useMediaQuery("(max-width:820px)");

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await getCpActivity(cp.cpId);
      if (res?.success) setHistory(res.data || []);
    } catch (e) {
      toast.error("Failed to load call history", { position: "top-center", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, [cp.cpId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!outcome) { toast.error("Select an outcome", { position: "top-center", autoClose: 3000 }); return; }
    if (!remark.trim()) { toast.error("Enter a remark", { position: "top-center", autoClose: 3000 }); return; }
    setSaving(true);
    try {
      const res = await addCpActivity(cp.cpId, { userId, outcome, remark: remark.trim() });
      if (res?.success) {
        toast.success("Remark saved", { position: "top-center", autoClose: 2000 });
        setOutcome(""); setRemark("");
        loadHistory();
        onSaved && onSaved();
      } else {
        toast.error(res?.message || "Failed to save remark", { position: "top-center", autoClose: 3000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save remark", { position: "top-center", autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const meta = TIER_META[cp.tier] || TIER_META.neverActive;

  // Phones get a full-screen page instead of the drawer. All the state and the
  // save above are shared, so only the presentation differs.
  if (isMobile) {
    return (
      <CpDetailMobile
        cp={cp}
        onClose={onClose}
        onCall={onCall}
        history={history}
        loading={loading}
        outcome={outcome}
        onOutcomeChange={setOutcome}
        remark={remark}
        onRemarkChange={setRemark}
        saving={saving}
        onSave={save}
      />
    );
  }

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 820 }, p: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>{cp.cpName || "—"}</Typography>
          <Typography sx={{ color: "#98A2B3", fontSize: "1.25rem" }}>#{cp.cpDisplayId}</Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        <Chip label={meta.label} size="small"
          sx={{ color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.border}`, fontWeight: 600 }} />
        {cp.daysDormant !== null && (
          <Typography sx={{ fontSize: "1.25rem", color: "#667085" }}>{cp.daysDormant} days dormant</Typography>
        )}
      </Box>

      <Typography sx={{ fontSize: "1.3rem", color: "#475467", mb: 0.5 }}>
        Contact: <b>{cp.contactPersonName || "—"}</b> {cp.phone ? `· ${cp.phone}` : "· no number"}
      </Typography>

      <Typography sx={{ fontSize: "1.3rem", color: "#475467", mb: 1 }}>
        CP Sourcing: <b>{cp.ownerName || "—"}</b>
      </Typography>

      <Button
        variant="contained" startIcon={<CallIcon />} disabled={!cp.phone} onClick={onCall}
        sx={{ textTransform: "none", my: 1.5, alignSelf: "flex-start" }}
      >
        {cp.phone ? "Call" : "No number"}
      </Button>

      <Divider sx={{ my: 1.5 }} />

      {/* Add remark */}
      <Typography fontWeight={600} sx={{ mb: 1, fontSize: "1.35rem" }}>Log this call</Typography>
      <Select size="small" displayEmpty fullWidth value={outcome} onChange={(e) => setOutcome(e.target.value)} sx={{ mb: 1.2 }}>
        <MenuItem value=""><em>Select outcome…</em></MenuItem>
        {CP_SOURCING_OUTCOMES.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </Select>
      <TextField
        size="small" fullWidth multiline minRows={3} placeholder="What was discussed…"
        value={remark} onChange={(e) => setRemark(e.target.value)} sx={{ mb: 1.2 }}
      />
      <Button variant="contained" onClick={save} disabled={saving} sx={{ textTransform: "none", mb: 2 }}>
        {saving ? "Saving…" : "Save remark"}
      </Button>

      <Divider sx={{ my: 1.5 }} />

      {/* History */}
      <Typography fontWeight={600} sx={{ mb: 1, fontSize: "1.35rem" }}>Discussion history</Typography>
      {loading && <Box sx={{ textAlign: "center", py: 2 }}><CircularProgress size={24} /></Box>}
      {!loading && history.length === 0 && (
        <Typography sx={{ color: "#667085", fontSize: "1.25rem" }}>
          No calls logged yet — this CP has never been contacted.
        </Typography>
      )}
      {history.map((h) => (
        <Box key={h.cpSourcingActivityLogId} sx={{ border: "1px solid #EAECF0", borderRadius: "8px", p: 1.5, mb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "1.25rem" }}>{h.outcome}</Typography>
            <Typography sx={{ color: "#98A2B3", fontSize: "1.15rem" }}>{fmtDateTime(h.created_at)}</Typography>
          </Box>
          {h.remark && <Typography sx={{ fontSize: "1.25rem", color: "#475467" }}>{h.remark}</Typography>}
          {h.userName && <Typography sx={{ fontSize: "1.15rem", color: "#98A2B3", mt: 0.5 }}>by {h.userName}</Typography>}
        </Box>
      ))}
    </Drawer>
  );
}
